import {
  OVERVIEW,
  PLAYBACKS,
  HEATMAPS,
  FEEDBACK,
  TEAM,
  SURVEY_TEMPLATES,
  HIGHLIGHTS,
  SETTINGS,
} from '@hotjar/webapp/constants/routes';
import { getRoute } from '@hotjar/webapp/utils/routing';
import { isAccountOwner } from '@hotjar/account';
import type { CurrentUser } from '@hotjar/user';
import {
  USER_EXPERTISE,
  USER_GOALS,
} from '@hotjar/webapp/domains/activation/user-personalization-info/types';
import { useUserPersonalizationInfoQuery } from '@hotjar/webapp/domains/activation/user-personalization-info/query';
import type { Step, GettingStartedGuideState } from '../types';
import { StepId, StepState } from '../types';

import RecordingsBanner from '../assets/recordings.jpg';
import HeatmapsBanner from '../assets/heatmaps.jpg';
import IncomingFeedbackBanner from '../assets/incoming_feedback.jpg';
import SurveysBanner from '../assets/surveys.jpg';
import FinishInstallationBanner from '../assets/finish_installation.jpg';
import InviteTeamMembersBanner from '../assets/invite_team_members.jpg';
import { STEPS } from '../../SetupWizard/steps/types';

export const genericSteps = [
  StepId.FinishInstallation,
  StepId.Recording,
  StepId.Heatmap,
  StepId.Survey,
  StepId.Feedback,
  StepId.Invite,
];

export const personalizedStepIds = {
  [USER_EXPERTISE.BEGINNER]: {
    [USER_GOALS.FEEDBACK]: [
      StepId.FinishInstallation,
      StepId.Feedback,
      StepId.Survey,
      StepId.Recording,
      StepId.Invite,
      StepId.Heatmap,
    ],
    [USER_GOALS.HEATMAPS]: [
      StepId.FinishInstallation,
      StepId.Heatmap,
      StepId.Recording,
      StepId.Survey,
      StepId.Invite,
      StepId.Feedback,
    ],
    [USER_GOALS.RECORDINGS]: [
      StepId.FinishInstallation,
      StepId.Recording,
      StepId.Heatmap,
      StepId.Survey,
      StepId.Invite,
      StepId.Feedback,
    ],
    [USER_GOALS.SKIPPED]: [
      StepId.FinishInstallation,
      StepId.Recording,
      StepId.Heatmap,
      StepId.Survey,
      StepId.Feedback,
      StepId.Invite,
    ],
    [USER_GOALS.SURVEYS]: [
      StepId.Survey,
      StepId.FinishInstallation,
      StepId.Feedback,
      StepId.Recording,
      StepId.Invite,
      StepId.Heatmap,
    ],
  },
  [USER_EXPERTISE.INTERMEDIATE]: {
    [USER_GOALS.FEEDBACK]: [
      StepId.FinishInstallation,
      StepId.Feedback,
      StepId.Survey,
      StepId.Recording,
      StepId.Heatmap,
      StepId.Invite,
    ],
    [USER_GOALS.HEATMAPS]: [
      StepId.FinishInstallation,
      StepId.Highlights,
      StepId.Heatmap,
      StepId.Invite,
      StepId.Survey,
      StepId.Feedback,
    ],
    [USER_GOALS.RECORDINGS]: [
      StepId.FinishInstallation,
      StepId.Highlights,
      StepId.GlobalFilters,
      StepId.Invite,
      StepId.Survey,
      StepId.Feedback,
    ],
    [USER_GOALS.SKIPPED]: [
      StepId.FinishInstallation,
      StepId.Recording,
      StepId.Heatmap,
      StepId.Survey,
      StepId.Feedback,
      StepId.Invite,
    ],
    [USER_GOALS.SURVEYS]: [
      StepId.Survey,
      StepId.FinishInstallation,
      StepId.Feedback,
      StepId.GlobalFilters,
      StepId.Invite,
      StepId.Heatmap,
    ],
  },
  [USER_EXPERTISE.EXPERT]: {
    [USER_GOALS.FEEDBACK]: [
      StepId.FinishInstallation,
      StepId.Feedback,
      StepId.Integrations,
      StepId.Recording,
      StepId.Heatmap,
      StepId.Invite,
    ],
    [USER_GOALS.HEATMAPS]: [
      StepId.FinishInstallation,
      StepId.Highlights,
      StepId.Integrations,
      StepId.GlobalFilters,
      StepId.Invite,
      StepId.Feedback,
    ],
    [USER_GOALS.RECORDINGS]: [
      StepId.FinishInstallation,
      StepId.Highlights,
      StepId.Integrations,
      StepId.Segments,
      StepId.Invite,
      StepId.Feedback,
    ],
    [USER_GOALS.SKIPPED]: [
      StepId.FinishInstallation,
      StepId.Recording,
      StepId.Heatmap,
      StepId.Survey,
      StepId.Feedback,
      StepId.Invite,
    ],
    [USER_GOALS.SURVEYS]: [
      StepId.Survey,
      StepId.FinishInstallation,
      StepId.Feedback,
      StepId.Integrations,
      StepId.Invite,
      StepId.Heatmap,
    ],
  },
  [USER_EXPERTISE.SKIP]: {
    [USER_GOALS.FEEDBACK]: [
      StepId.FinishInstallation,
      StepId.Feedback,
      StepId.Recording,
      StepId.Heatmap,
      StepId.Survey,
      StepId.Invite,
    ],
    [USER_GOALS.HEATMAPS]: [
      StepId.FinishInstallation,
      StepId.Heatmap,
      StepId.Recording,
      StepId.Survey,
      StepId.Feedback,
      StepId.Invite,
    ],
    [USER_GOALS.RECORDINGS]: [
      StepId.FinishInstallation,
      StepId.Recording,
      StepId.Heatmap,
      StepId.Survey,
      StepId.Feedback,
      StepId.Invite,
    ],
    [USER_GOALS.SKIPPED]: [
      StepId.FinishInstallation,
      StepId.Recording,
      StepId.Heatmap,
      StepId.Survey,
      StepId.Feedback,
      StepId.Invite,
    ],
    [USER_GOALS.SURVEYS]: [
      StepId.FinishInstallation,
      StepId.Survey,
      StepId.Feedback,
      StepId.Recording,
      StepId.Invite,
      StepId.Heatmap,
    ],
  },
};

export interface StepsInfo {
  steps: Array<Step>;
  completed: number;
  total: number;
}

export function useSteps(
  state: GettingStartedGuideState,
  user?: CurrentUser,
): {
  steps: Array<Step>;
  stepsMap: { [id in StepId]: Step };
  isStepsReady: boolean;
} {
  const { site } = state;
  const siteId = site?.id;
  const userIsAccountOwner = isAccountOwner(user?.id, site?.account);
  const { data: userPersonalizationInfo, isFetched: isUserPersonalizationInfoFetched } =
    useUserPersonalizationInfoQuery();

  const stepsMap: { [id in StepId]: Step } = {
    [StepId.Welcome]: {
      id: StepId.Welcome,
      icon: 'hotjar',
      banner: FinishInstallationBanner,
      title: 'activation:getting-started.tips.welcome.title',
      subtitle: 'activation:getting-started.tips.welcome.subtitle',
      content: 'activation:getting-started.tips.welcome.content',
      cta: {
        title: 'activation:getting-started.tips.welcome.cta',
        url: 'https://www.youtube.com/watch?v=YsmMn5MWDN4',
        icon: 'play',
        isExternal: true,
        completeOnClick: true,
      },
      get state() {
        return state.welcomeVideoSeen ? StepState.Success : StepState.Pending;
      },
    },
    [StepId.FinishInstallation]: {
      id: StepId.FinishInstallation,
      icon: 'rocketship',
      banner: FinishInstallationBanner,
      title: 'activation:getting-started.tips.tracking-code.title',
      subtitle: 'activation:getting-started.tips.tracking-code.subtitle',
      content: 'activation:getting-started.tips.tracking-code.content',
      get cta() {
        return siteId
          ? {
              title: 'activation:getting-started.tips.tracking-code.install',
              url: `${getRoute(OVERVIEW.SETUP.path, { siteId })}?step=${STEPS.INSTALLATION_METHOD}`,
            }
          : undefined;
      },
      get state() {
        return state.trackingCodeInstalled ? StepState.Success : StepState.Pending;
      },
    },
    [StepId.RecordingHeatmap]: {
      id: StepId.RecordingHeatmap,
      icon: 'flame',
      banner: RecordingsBanner,
      title: 'activation:getting-started.tips.recordings-heatmaps.title',
      subtitle: 'activation:getting-started.tips.recordings-heatmaps.subtitle',
      content: 'activation:getting-started.tips.recordings-heatmaps.content',
      get cta() {
        return siteId
          ? {
              title: 'activation:getting-started.tips.recordings-heatmaps.cta',
              url: getRoute(PLAYBACKS.BASE.path, { siteId }),
            }
          : undefined;
      },
      get ctaSecondary() {
        return siteId
          ? {
              title: 'activation:getting-started.tips.recordings-heatmaps.cta-secondary',
              url: getRoute(HEATMAPS.LIST_CONTINUOUS_CAPTURE.path, { siteId }),
            }
          : undefined;
      },
      get state() {
        if (state.recordingViewed || state.heatmapViewed) {
          return StepState.Success;
        }

        return StepState.Pending;
      },
    },
    [StepId.Recording]: {
      id: StepId.Recording,
      icon: 'recordings_old',
      banner: RecordingsBanner,
      title: 'activation:getting-started.tips.recordings.title',
      subtitle: 'activation:getting-started.tips.recordings.subtitle',
      content: 'activation:getting-started.tips.recordings.content',
      get cta() {
        return siteId
          ? {
              title: 'activation:getting-started.tips.recordings.cta',
              url: getRoute(PLAYBACKS.BASE.path, { siteId }),
            }
          : undefined;
      },
      get state() {
        if (state.recordingViewed) {
          return StepState.Success;
        }

        return StepState.Pending;
      },
    },
    [StepId.Heatmap]: {
      id: StepId.Heatmap,
      icon: 'flame',
      banner: HeatmapsBanner,
      title: 'activation:getting-started.tips.heatmaps.title',
      subtitle: 'activation:getting-started.tips.heatmaps.subtitle',
      content: 'activation:getting-started.tips.heatmaps.content',
      get cta() {
        return siteId
          ? {
              title: 'activation:getting-started.tips.heatmaps.cta',
              url: getRoute(HEATMAPS.LIST_CONTINUOUS_CAPTURE.path, { siteId }),
            }
          : undefined;
      },
      get state() {
        if (state.heatmapViewed) {
          return StepState.Success;
        }

        return StepState.Pending;
      },
    },
    [StepId.Feedback]: {
      id: StepId.Feedback,
      icon: 'feedback',
      banner: IncomingFeedbackBanner,
      title: 'activation:getting-started.tips.feedback.title',
      subtitle: 'activation:getting-started.tips.feedback.subtitle',
      content: 'activation:getting-started.tips.feedback.content',
      get cta() {
        return siteId
          ? {
              title: 'activation:getting-started.tips.feedback.cta',
              url: getRoute(FEEDBACK.CREATE.path, { siteId }),
            }
          : undefined;
      },
      get state() {
        if (state.incomingFeedbackCreated) {
          return StepState.Success;
        }
        if (!state.isFeedbackEnabled) {
          return StepState.Skipped;
        }

        return StepState.Pending;
      },
    },
    [StepId.Survey]: {
      id: StepId.Survey,
      icon: 'chart_bar',
      banner: SurveysBanner,
      title: 'activation:getting-started.tips.surveys.title',
      subtitle: 'activation:getting-started.tips.surveys.subtitle',
      content: 'activation:getting-started.tips.surveys.content',
      get cta() {
        return siteId
          ? {
              title: 'activation:getting-started.tips.surveys.cta',
              url: getRoute(SURVEY_TEMPLATES.BASE.path, {
                siteId,
              }),
            }
          : undefined;
      },
      get state() {
        if (state.surveyLaunched) {
          return StepState.Success;
        }
        if (!state.isFeedbackEnabled) {
          return StepState.Skipped;
        }

        return StepState.Pending;
      },
    },
    [StepId.Highlights]: {
      id: StepId.Highlights,
      icon: 'lightbulb',
      banner: HeatmapsBanner,
      title: 'activation:getting-started.tips.highlights.title',
      subtitle: 'activation:getting-started.tips.highlights.title',
      content: 'activation:getting-started.tips.highlights.content',
      get cta() {
        return siteId
          ? {
              title: 'activation:getting-started.tips.highlights.cta',
              url: getRoute(HIGHLIGHTS.LIST.path, { siteId }),
            }
          : undefined;
      },
      get state() {
        if (state?.heatmapViewed) {
          return StepState.Success;
        }

        return StepState.Pending;
      },
    },
    [StepId.Integrations]: {
      id: StepId.Integrations,
      icon: 'puzzle',
      banner: HeatmapsBanner,
      title: 'activation:getting-started.tips.integrations.title',
      subtitle: 'activation:getting-started.tips.integrations.content',
      content: 'activation:getting-started.tips.integrations.content',
      get cta() {
        return siteId
          ? {
              title: 'activation:getting-started.tips.integrations.cta',
              url: getRoute(SETTINGS.INTEGRATIONS.path, { siteId }),
              completeOnClick: true,
            }
          : undefined;
      },
      get state() {
        if (state?.isIntegrationsViewed) {
          return StepState.Success;
        }

        return StepState.Pending;
      },
    },
    [StepId.Invite]: {
      id: StepId.Invite,
      icon: 'user_add',
      banner: InviteTeamMembersBanner,
      title: 'activation:getting-started.tips.invite.title',
      subtitle: 'activation:getting-started.tips.invite.subtitle',
      content: 'activation:getting-started.tips.invite.content',
      get cta() {
        return siteId
          ? {
              title: 'activation:getting-started.tips.invite.cta',
              url: getRoute(TEAM.INVITE.path, { siteId }),
            }
          : undefined;
      },
      get state() {
        if (state.inviteCompleted) {
          return StepState.Success;
        }
        if (state.inviteSkipped || !state.isInviteEnabled) {
          return StepState.Skipped;
        }

        return StepState.Pending;
      },
    },
    [StepId.Segments]: {
      id: StepId.Segments,
      icon: 'recordings_old',
      banner: HeatmapsBanner,
      title: 'activation:getting-started.tips.segments.title',
      subtitle: 'activation:getting-started.tips.segments.subtitle',
      content: 'activation:getting-started.tips.segments.content',
      get cta() {
        return siteId
          ? {
              title: 'activation:getting-started.tips.segments.cta',
              url: getRoute(PLAYBACKS.LIST.path, { siteId }),
              completeOnClick: true,
            }
          : undefined;
      },
      get state() {
        if (state.isSegmentsViewed) {
          return StepState.Success;
        }

        return StepState.Pending;
      },
    },
    [StepId.LocalFilters]: {
      id: StepId.LocalFilters,
      icon: 'funnel',
      banner: HeatmapsBanner,
      title: 'activation:getting-started.tips.local-filters.title',
      subtitle: 'activation:getting-started.tips.local-filters.subtitle',
      content: 'activation:getting-started.tips.local-filters.content',
      get cta() {
        return siteId
          ? {
              title: 'activation:getting-started.tips.local-filters.cta',
              url: getRoute(SURVEY_TEMPLATES.BASE.path, { siteId }),
              completeOnClick: true,
            }
          : undefined;
      },
      get state() {
        if (state.isLocalFiltersViewed) {
          return StepState.Success;
        }

        return StepState.Pending;
      },
    },
    [StepId.GlobalFilters]: {
      id: StepId.GlobalFilters,
      icon: 'funnel',
      banner: HeatmapsBanner,
      title: 'activation:getting-started.tips.global-filters.title',
      subtitle: 'activation:getting-started.tips.global-filters.title',
      content: 'activation:getting-started.tips.global-filters.content',
      get cta() {
        return siteId
          ? {
              title: 'activation:getting-started.tips.global-filters.cta',
              url: getRoute(PLAYBACKS.BASE.path, { siteId }),
              completeOnClick: true,
            }
          : undefined;
      },
      get state() {
        if (state?.isGlobalFiltersViewed) {
          return StepState.Success;
        }

        return StepState.Pending;
      },
    },
  };

  const accountOwnerStep = userIsAccountOwner ? [] : [StepId.Welcome];

  const getStepsList = (): Array<StepId> => {
    if (!isUserPersonalizationInfoFetched) {
      return genericSteps;
    }

    const { goal, expertise } = userPersonalizationInfo || {};

    if (goal && expertise) {
      if (goal === USER_GOALS.SKIPPED) {
        return genericSteps;
      }

      return [...accountOwnerStep, ...personalizedStepIds[expertise][goal]];
    }

    return [...accountOwnerStep, ...genericSteps];
  };

  const mapStepsList = () =>
    getStepsList().reduce((steps, id) => {
      const step = stepsMap[id];
      if (step.state !== StepState.Skipped) {
        steps.push(step);
      }
      return steps;
    }, [] as Array<Step>);

  return {
    steps: mapStepsList(),
    stepsMap,
    isStepsReady: mapStepsList().length > 0,
  };
}
